/**
 * DeviceMapPanel — SAAS-TENANT-PORTAL-MAP-1 (extraído de TenantsPage/TenantMapTab).
 *
 * Painel de mapa reaproveitável, usado em DOIS lugares:
 *   - área admin Empresas → detalhe do tenant (recebe `tenantId`, platform admin
 *     filtra por empresa via ?tenant_id=);
 *   - portal do cliente → página Mapa (SEM `tenantId`; o backend escopa por
 *     ctx.tenant_id → cada tenant só vê a própria frota, super_admin vê global).
 *
 * Fetch = getFleetMapDevices({ tenantId }); quando tenantId é undefined a
 * querystring não leva tenant_id (segurança: nunca X-Tenant; o escopo é do Bearer).
 * Centro do mapa em localStorage por chave (tenantId || 'self'); sem centro salvo,
 * o TenantMiniMap faz fit-bounds nos devices geolocalizados.
 */
import { useEffect, useMemo, useState } from "react";
import { getFleetMapDevices } from "../../lib/api.js";
import TenantMiniMap from "./TenantMiniMap.jsx";

function Kpi({ label, value, tone }) {
  return (
    <div className="glass-card-pad">
      <div className={`text-2xl font-bold ${tone || ""}`}>{value}</div>
      <div className="text-xs text-noc-muted">{label}</div>
    </div>
  );
}

export default function DeviceMapPanel({ tenantId, onNavigate }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all"); // all|online|offline|protected|no_geo
  const [query, setQuery] = useState("");

  const CENTER_KEY = `mapCenter:${tenantId || "self"}`;
  const [center, setCenter] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CENTER_KEY) || "null"); }
    catch { return null; }
  });
  const [viewport, setViewport] = useState(null);
  const [showCenterModal, setShowCenterModal] = useState(false);

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true); setErr(null);
    getFleetMapDevices({ tenantId, signal: ac.signal })
      .then((d) => setDevices(d.devices || []))
      .catch((e) => { if (e?.name !== "AbortError") setErr(e?.message || "Falha ao carregar o mapa."); })
      .finally(() => setLoading(false));
    return () => ac.abort();
  }, [tenantId]);

  const hasGeo = (d) => typeof d.lat === "number" && typeof d.lng === "number";

  // KPIs (sobre TODA a frota do escopo, não só a filtrada)
  const kpi = useMemo(() => {
    const total = devices.length;
    const geo = devices.filter(hasGeo).length;
    const online = devices.filter((d) => d.online).length;
    return { total, geo, noGeo: total - geo, online, offline: total - online };
  }, [devices]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return devices.filter((d) => {
      if (statusFilter === "online" && !d.online) return false;
      if (statusFilter === "offline" && d.online) return false;
      if (statusFilter === "protected" && !d.effective_protected) return false;
      if (statusFilter === "no_geo" && hasGeo(d)) return false;
      if (!q) return true;
      return [d.device_id, d.customer_name, d.mac]
        .some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [devices, statusFilter, query]);

  const noGeo = visible.filter((d) => !hasGeo(d));
  const geoCount = visible.length - noGeo.length;

  function saveCenter(c) {
    localStorage.setItem(CENTER_KEY, JSON.stringify(c));
    setCenter(c); setShowCenterModal(false);
  }
  function clearCenter() {
    localStorage.removeItem(CENTER_KEY);
    setCenter(null); setShowCenterModal(false);
  }

  if (loading) {
    return <div className="text-sm text-noc-muted glass-card-pad">Carregando mapa…</div>;
  }
  if (err) {
    return <div className="text-sm text-noc-err bg-noc-err/10 border border-noc-err/30 rounded-lg p-3">{err}</div>;
  }

  // estado vazio total
  if (devices.length === 0) {
    return (
      <div className="glass-card-pad text-sm text-noc-muted text-center py-10">
        Nenhum dispositivo nesta empresa ainda.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Kpi label="Total" value={kpi.total} />
        <Kpi label="Com localização" value={kpi.geo} />
        <Kpi label="Sem localização" value={kpi.noGeo} />
        <Kpi label="Online" value={kpi.online} tone="text-noc-ok" />
        <Kpi label="Offline" value={kpi.offline} tone="text-noc-muted" />
      </div>

      {/* toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="flex rounded-lg border border-noc-border overflow-hidden text-xs w-fit flex-wrap">
          {[["all", `Todos (${visible.length})`], ["online", "Online"],
            ["offline", "Offline"], ["protected", "Protegidos"],
            ["no_geo", "Sem localização"]].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setStatusFilter(k)}
              className={`px-2.5 py-1 ${statusFilter === k
                ? "bg-noc-accent/20 text-noc-accent font-semibold"
                : "text-noc-muted hover:text-noc-text"}`}>
              {label}
            </button>
          ))}
        </div>
        <input type="search" value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar device_id / cliente / MAC…"
          className="h-8 px-2.5 rounded-lg bg-noc-bg border border-noc-border text-xs text-noc-text sm:w-56" />
        <div className="flex-1" />
        <button type="button" onClick={() => setShowCenterModal(true)}
          className="h-8 px-3 rounded-lg text-xs border border-noc-border text-noc-text">
          📍 Definir centro do mapa
        </button>
      </div>

      {geoCount === 0 ? (
        <div className="glass-card-pad text-sm text-noc-muted text-center py-8">
          Nenhum dispositivo com localização cadastrada ainda.
        </div>
      ) : (
        <TenantMiniMap
          devices={visible}
          center={center}
          onSelect={(deviceId) => onNavigate?.("detail", deviceId)}
          onViewportChange={setViewport}
        />
      )}

      <div className="text-[11px] text-noc-muted flex flex-wrap gap-x-4 gap-y-1">
        <span>{geoCount} no mapa · {noGeo.length} sem localização</span>
        {center && <span>Centro salvo neste navegador ({center.lat}, {center.lng})</span>}
      </div>

      {/* sem localização */}
      {noGeo.length > 0 && (
        <div className="glass-card-pad">
          <div className="text-xs font-semibold uppercase tracking-wide text-noc-muted mb-2">
            Sem localização ({noGeo.length})
          </div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {noGeo.map((d) => (
              <button key={d.device_id} type="button"
                onClick={() => onNavigate?.("detail", d.device_id)}
                className="w-full text-left flex items-center gap-2 text-xs hover:bg-noc-bg/40 rounded px-1 py-0.5">
                <span className={`w-2 h-2 rounded-full shrink-0 ${d.online ? "bg-noc-ok" : "bg-noc-muted"}`} />
                <span className="font-mono">{d.device_id}</span>
                {d.effective_protected && <span className="text-noc-purple">🔒</span>}
                <span className="text-noc-muted truncate">{d.customer_name || ""}</span>
                <span className="flex-1" />
                <span className="text-noc-accent">abrir detalhe →</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {showCenterModal && (
        <MapCenterModal
          current={viewport || center}
          onSave={saveCenter}
          onClear={center ? clearCenter : null}
          onClose={() => setShowCenterModal(false)}
        />
      )}
    </div>
  );
}

/* ──── Modal do centro do mapa (localStorage por empresa) ──── */
function MapCenterModal({ current, onSave, onClear, onClose }) {
  const [lat, setLat] = useState(current?.lat ?? "");
  const [lng, setLng] = useState(current?.lng ?? "");
  const [zoom, setZoom] = useState(current?.zoom ?? 12);

  function submit() {
    const la = Number(lat), ln = Number(lng), zo = Number(zoom);
    if (!Number.isFinite(la) || !Number.isFinite(ln)) return;
    onSave({ lat: +la.toFixed(5), lng: +ln.toFixed(5), zoom: Math.min(19, Math.max(3, zo || 12)) });
  }

  return (
    <div className="fixed inset-0 z-[2100] bg-black/70 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-noc-panel border border-noc-accent/30 rounded-lg shadow-2xl w-full max-w-sm p-4 space-y-3">
        <div className="text-sm font-semibold text-noc-accent">Cidade central do mapa</div>
        <p className="text-[11px] text-noc-muted">
          Latitude/longitude do centro inicial do mapa. Salvo <b>neste navegador</b>
          {" "}(fase 1). Use o botão abaixo para capturar a posição atual do mapa.
        </p>
        <label className="block text-xs space-y-1">
          <span className="text-noc-muted">Latitude</span>
          <input className="installer-input" value={lat}
            onChange={(e) => setLat(e.target.value)} placeholder="-10.6657" />
        </label>
        <label className="block text-xs space-y-1">
          <span className="text-noc-muted">Longitude</span>
          <input className="installer-input" value={lng}
            onChange={(e) => setLng(e.target.value)} placeholder="-39.5493" />
        </label>
        <label className="block text-xs space-y-1">
          <span className="text-noc-muted">Zoom (3–19)</span>
          <input className="installer-input" type="number" min={3} max={19} value={zoom}
            onChange={(e) => setZoom(e.target.value)} />
        </label>
        {current && (
          <button type="button"
            onClick={() => { setLat(current.lat); setLng(current.lng); setZoom(current.zoom || 12); }}
            className="text-[11px] text-noc-accent underline">
            Usar a posição atual do mapa ({current.lat}, {current.lng})
          </button>
        )}
        <div className="flex justify-between gap-2 pt-1">
          {onClear ? (
            <button type="button" onClick={onClear}
              className="h-8 px-3 rounded-lg text-xs border border-noc-border text-noc-muted">
              Voltar ao centro da frota
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button type="button" onClick={onClose}
              className="h-8 px-3 rounded-lg text-xs border border-noc-border text-noc-muted">
              Cancelar
            </button>
            <button type="button" onClick={submit}
              className="h-8 px-3 rounded-lg text-xs font-semibold bg-noc-accent/15 text-noc-accent border border-noc-accent/40">
              Salvar centro
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
